// R220 — **THE ONE OFFER THAT SAVES A SUBJECT TO PEOPLE OF INTEREST, AND THE THREE-WAY ANSWER
// UNDER IT.** Nine call sites render this component — Find Your Type, both mini-report shells, the
// prediction and deep-report modals, the astrology reading modal, the tarot subject shell and Human
// Design — and inside `src/App.tsx` nothing could reach either half of it: `savePersonFromChart` is
// a PURE three-way law (R18: saved / duplicate / failed) that no `.mjs` gate could load, and the
// component that speaks its three sentences could not be imported by one either.
//
// Moved VERBATIM (the R8 ledger's own requirement — no member-visible line changed with the move),
// and it holds two hooks, so it is the `TierCards` / `MicButton` / `SignInModal` class rather than a
// hookless leaf a gate CALLS: **the reach it buys is the IMPORT.**
import { useState } from "react";
import { DuplicatePersonPrompt, type SubjectPickerPerson, type DuplicateChoice } from "./DuplicatePersonPrompt";
import { RESOLUTION_CANCEL } from "../personDuplicate.js";
import { addPerson } from "../lib/people";
import { combineName } from "../nameParts.js";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";
import type { BirthChartForm } from "./BirthFields";
import type { AstrologyReadingForm } from "../lib/reading-form";

// Save the currently-entered "someone new" subject to the member's People of Interest, so the
// site learns them for next time. Returns the created person (or null).
// I6 RULE 3 (owner, 2026-07-16): the server refuses a silent duplicate and hands back the person
// the member already has. `onDuplicate` carries their answer ("replace" keeps the same id so
// existing readings stay attached; "keep-both" saves a second row deliberately). Returning the
// duplicate lets the shared save path ask warmly instead of failing quietly.
export type SavePersonResult =
  | { status: "saved"; person: SubjectPickerPerson }
  | { status: "duplicate"; existing: SubjectPickerPerson }
  | { status: "failed" };

export async function savePersonFromChart(
  chart: BirthChartForm | AstrologyReadingForm,
  onDuplicate?: DuplicateChoice,
): Promise<SavePersonResult> {
  const name = combineName(chart.firstName, chart.middleName, chart.lastName) || chart.name;
  if (!name.trim()) return { status: "failed" };
  // R52: one transport, and the three statuses this offer already spoke are DERIVED from its answer
  // rather than re-read off the payload — the My Room form's silence is what happens when a second
  // reader of this route writes its own.
  const answer = await addPerson<SubjectPickerPerson>({ ...chart, name, ...(onDuplicate ? { onDuplicate } : {}) });
  if (answer.ok) return { status: "saved", person: answer.person };
  if (answer.kind === "duplicate") return { status: "duplicate", existing: answer.existing };
  return { status: "failed" };
}

// The "Someone new" save offer. Every generator wired with the SubjectPicker shows this
// after a reading when the subject was entered fresh (kind === "new"), so a member is
// offered "save to People of Interest" exactly once, with the same values they just read.
// Reused across Find Your Type, numerology, astrology readings — one component, one offer.
export function SaveSubjectOffer({ pickKind, chart, language = DEFAULT_LANGUAGE }: { pickKind: string; chart: BirthChartForm | AstrologyReadingForm; language?: string }) {
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  // I6 Rule 3: the duplicate the server handed back, held while the member decides.
  const [dupe, setDupe] = useState<SubjectPickerPerson | null>(null);
  if (pickKind !== "new") return null;
  const name = combineName(chart.firstName, chart.middleName, chart.lastName) || chart.name;
  if (!name.trim() || !chart.birthDate) return null;
  const save = async (onDuplicate?: DuplicateChoice) => {
    setState("saving");
    const res = await savePersonFromChart(chart, onDuplicate);
    if (res.status === "duplicate") { setDupe(res.existing); setState("idle"); return; }
    setDupe(null);
    setState(res.status === "saved" ? "saved" : "idle");
  };
  return (
    <>
      <button
        type="button"
        className="button button--ghost"
        disabled={state !== "idle"}
        onClick={() => save()}
      >
        {state === "saved" ? t(language, "subject.saved", { name }) : state === "saving" ? t(language, "result.saving") : t(language, "subject.save", { name })}
      </button>
      {dupe && (
        <DuplicatePersonPrompt
          existing={dupe}
          onChoose={(choice) => { if (choice === RESOLUTION_CANCEL) { setDupe(null); return; } save(choice); }}
        />
      )}
    </>
  );
}
